import { Component, OnInit, ViewChild } from '@angular/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppTagConfiguratorComponent } from '@datachain/ui-sdk/components/app-tag-configurator';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';

import { TagSelector } from '../../../core/store';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { AppMetadataEntity } from '../../../ui/components/app-metadata/app-metadata.entity';
import {
	closeCreateDepotModal,
	createSubscriptionForRepository,
	DepotConfigSelector,
	fetchAvailableSubscriptionsForDepotCreation,
	initDepotCreation,
} from '../../store';
import { SubscriptionForDepotItemEntity } from './available-subscription-item.entity';
import { SubscriptionListEntity } from './available-subscriptions-list.entity';

@Component({
	selector: 'app-depot-creation-stepper',
	templateUrl: './depot-creation-modal.component.html',
	styleUrls: ['./depot-creation-modal.component.scss'],
})
export class DepotCreationModalComponent
	extends DcBaseComponent
	implements OnInit
{
	public Appearance: MatFormFieldAppearance = 'outline';

	protected readonly GdIcons = GdIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly DcIcons = DcIcons;

	public initialMetadata = AppMetadataEntity.build({});
	public allowProducerVersionSwitch = true;

	public vo$: Observable<{
		subscriptionsList: SubscriptionListEntity | null;
		availableTags: OrderedSet<AppTagItemEntity>;
		hasSelection: boolean;
		isFirstLoad: boolean;
		isMetadataValid: boolean;
		isCreationInProgress: boolean;
	}>;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);
	private readonly metadataIsValidSubject = new BehaviorSubject<boolean>(false);
	private readonly hasSelectionSubject =
		new BehaviorSubject<SubscriptionForDepotItemEntity | null>(null);

	@ViewChild(DxDataGridComponent)
	private readonly visibleGrid: DxDataGridComponent | null = null;

	@ViewChild(AppTagConfiguratorComponent)
	public tagConfiguratorComponentCmp: AppTagConfiguratorComponent | null = null;

	public constructor(
		private readonly store: Store,
		private readonly depotConfigSelector: DepotConfigSelector,
		private readonly tagSelector: TagSelector
	) {
		super();
		this.cmpId = 'depot-creation-modal';
		this.vo$ = combineAll({
			isMetadataValid: this.metadataIsValidSubject,
			isCreationInProgress: false,
			isFirstLoad: this.firstLoadSubject,
			hasSelection: this.hasSelectionSubject.pipe(
				map((selection) => selection !== null)
			),
			availableTags: this.tagSelector.getTags$(),
			subscriptionsList:
				this.depotConfigSelector.getAvailableSubscriptionsForDepotCreation$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
	}

	public close(): void {
		this.store.dispatch(closeCreateDepotModal());
	}

	public onSelection($ev: SelectionChangedEvent): void {
		if ($ev.selectedRowKeys.length === 1) {
			this.hasSelectionSubject.next($ev.selectedRowsData[0]);
		}
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			this.firstLoadSubject.next(false);
		}
	}

	public search(val: string): void {
		if (this.visibleGrid === null) {
			return;
		}
		(this.visibleGrid as DxDataGridComponent).instance.searchByText(val.trim());
		return;
	}

	public refresh(): void {
		this.store.dispatch(fetchAvailableSubscriptionsForDepotCreation());
	}

	public metadataHasError(hasErrors: boolean): void {
		return this.metadataIsValidSubject.next(!hasErrors);
	}

	public async createSubscriptionForRepo(
		metadata: AppMetadataEntity | null
	): Promise<void> {
		const selection = await firstValueFrom(this.hasSelectionSubject);

		if (!metadata || !selection) {
			return;
		}
		let tags = new Array<AppTagItemEntity>();
		if (this.tagConfiguratorComponentCmp) {
			tags = await this.tagConfiguratorComponentCmp.getUsedTags();
		}
		const metadataWithTags = metadata.setTags(tags);

		this.store.dispatch(
			createSubscriptionForRepository({
				metadata: metadataWithTags,
				subscription: selection,
				allowProducerVersionSwitch: this.allowProducerVersionSwitch,
			})
		);
	}

	private triggerInit(): void {
		this.store.dispatch(initDepotCreation());
		this.store.dispatch(fetchAvailableSubscriptionsForDepotCreation());
	}
}
